import { useCallback, useEffect, useMemo, useState } from "react";

import { createDirection, deleteDirection, fetchDirections, updateDirection } from "@/features/admin/api";
import type { AdminDirection } from "@/features/admin/types";
import { useAdminSession } from "@/features/admin/useAdminSession";
import PaginationControls from "./PaginationControls";

const DIRECTIONS_PER_PAGE = 4;

export default function DirectionsTab() {
  const { request } = useAdminSession();
  const [directions, setDirections] = useState<AdminDirection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newDirection, setNewDirection] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const loadDirections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextDirections = await fetchDirections(request);
      setDirections(nextDirections);
      setDrafts(Object.fromEntries(nextDirections.map((direction) => [direction.id, direction.nom])));
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les directions.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadDirections();
  }, [loadDirections]);

  const totalPages = Math.max(1, Math.ceil(directions.length / DIRECTIONS_PER_PAGE));
  const paginatedDirections = useMemo(() => {
    const startIndex = (currentPage - 1) * DIRECTIONS_PER_PAGE;
    return directions.slice(startIndex, startIndex + DIRECTIONS_PER_PAGE);
  }, [currentPage, directions]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2500);
  };

  const handleCreate = async () => {
    if (!newDirection.trim()) {
      setError("Saisissez le nom de la direction.");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      await createDirection(request, newDirection.trim());
      setNewDirection("");
      showToast("Direction créée");
      await loadDirections();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Création impossible.");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (directionId: string) => {
    const nextName = drafts[directionId]?.trim() ?? "";

    if (!nextName) {
      setError("Le nom de la direction est obligatoire.");
      return;
    }

    setEditingId(directionId);
    setError(null);

    try {
      await updateDirection(request, directionId, nextName);
      showToast("Direction mise à jour");
      await loadDirections();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Mise à jour impossible.");
    } finally {
      setEditingId(null);
    }
  };

  const handleDelete = async (directionId: string) => {
    setEditingId(directionId);
    setError(null);

    try {
      await deleteDirection(request, directionId);
      showToast("Direction supprimée");
      await loadDirections();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Suppression impossible.");
    } finally {
      setEditingId(null);
    }
  };

  return (
    <div className="space-y-6 relative">
      {toast ? (
        <div className="fixed top-6 right-6 z-50 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Directions</h1>
        <p className="mt-1 text-sm text-gray-500">Gérez les directions affichées dans le formulaire d&apos;adhésion et réutilisées dans l&apos;application.</p>
        {error ? <p className="mt-2 text-sm text-red-500">{error}</p> : null}
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex flex-col gap-3 md:flex-row">
          <input
            type="text"
            value={newDirection}
            onChange={(event) => setNewDirection(event.target.value)}
            placeholder="Nouvelle direction..."
            className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#E8521A]"
          />
          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating}
            className="rounded-xl bg-[#E8521A] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creating ? "Création..." : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-20 animate-pulse rounded-2xl border border-gray-100 bg-white"></div>
          ))
        ) : directions.length ? (
          paginatedDirections.map((direction) => {
            const busy = editingId === direction.id;

            return (
              <div key={direction.id} className="rounded-2xl border border-gray-100 bg-white p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <input
                    type="text"
                    value={drafts[direction.id] ?? direction.nom}
                    onChange={(event) => setDrafts((current) => ({ ...current, [direction.id]: event.target.value }))}
                    className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-[#E8521A]"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void handleUpdate(direction.id)}
                      disabled={busy}
                      className="rounded-xl border border-orange-200 px-4 py-3 text-sm font-medium text-[#E8521A] transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(direction.id)}
                      disabled={busy}
                      className="rounded-xl border border-red-200 px-4 py-3 text-sm font-medium text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            Aucune direction n&apos;est encore enregistrée.
          </div>
        )}
      </div>

      {!loading && directions.length > DIRECTIONS_PER_PAGE ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="directions"
        />
      ) : null}
    </div>
  );
}
